import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../UserRole/UserList.css';
import DeleteUserModal from '../modal/DeleteUserModal';
import Swal from 'sweetalert2';
import { useUser } from '../context/UserContext';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import { hasPrivilege } from '../utils/hasPrivilege';
const UserList = () => {
  const navigate = useNavigate();
  const { userData } = useUser();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('auth-token');
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(null);
      }
    };

    if (dropdownOpen !== null) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [dropdownOpen]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(process.env.REACT_APP_API_ASSIGN_USER, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      setUsers(data.data.users);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const headers = [
    { label: "Name", key: "name" },
    { label: "Email", key: "email" },
    { label: "User Type", key: "userType" },
    { label: "Role", key: "role" },
  ];

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const filteredUsers = users.filter((user) =>
    Object.values(user).some((value) =>
      value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleSort = (columnKey) => {
    let direction = "ascending";
    if (sortConfig.key === columnKey && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key: columnKey, direction });
  };

  const sortData = (data) => {
    const { key, direction } = sortConfig;
    if (!key) return data;

    return [...data].sort((a, b) => {
      let valueA, valueB;

      if (key === 'mobile') {
        valueA = (a.mobile_number || '').replace(/[+\s]/g, '');
        valueB = (b.mobile_number || '').replace(/[+\s]/g, '');
      } else if (key === 'userType') {
        valueA = (a.user_type || '').toLowerCase();
        valueB = (b.user_type || '').toLowerCase();
      } else if (key === 'role') {
        valueA = (a.roles || []).join(', ').toLowerCase();
        valueB = (b.roles || []).join(', ').toLowerCase();
      } else if (key === 'name') {
        valueA = (a.name || '').trim().toLowerCase();
        valueB = (b.name || '').trim().toLowerCase();
      } else {
        valueA = a[key] || '';
        valueB = b[key] || '';
      }
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return direction === "ascending"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
      if (valueA < valueB) return direction === "ascending" ? -1 : 1;
      if (valueA > valueB) return direction === "ascending" ? 1 : -1;
      return 0;
    });
  };

  const sortedData = sortData(filteredUsers);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const closeDeleteModal = () => setShowDeleteModal(false);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const handleCreateUser = () => navigate('/create-user');

  const handleEditClick = (user) => {
    navigate('/create-user', {
      state: {
        userId: user.id,
        name: user.name,
        email: user.email,
        mobile: user.mobile_number,
        userType: user.user_type,
        roles: user.roles,
        academyIds: user.academy_ids,
        academyNames: user.academy_names,
      },
    });
    setDropdownOpen(null);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
    setDropdownOpen(null);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    const apiUrlBase = process.env.REACT_APP_API_USER_DELETE;
    const apiUrl = `${apiUrlBase}?userId=${userToDelete.id}`;

    try {
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.text();
        if (data) {
          const jsonData = JSON.parse(data);
        }

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'User deleted successfully!',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });

        setShowDeleteModal(false);
        setUsers((prevUsers) =>
          prevUsers.filter((user) => user.id !== userToDelete.id)
        );

      } else {

        const error = await response.json();
        setShowDeleteModal(false);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: error.message || 'Failed to delete user.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      }

    } catch (error) {
      console.error('Delete API Error:', error);
      setShowDeleteModal(false);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error?.message || 'An error occurred while deleting the user.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };


  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  const totalEntries = filteredUsers.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  return (
    <div className="enrollment-container">

      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        {hasPrivilege(userData, 'USER_ROLE', 'USER_LIST_MODIFY') && (
          <button
            className="create-button-new"
            onClick={handleCreateUser}
          >
            <span className="create-button__icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="23"
                height="23"
                className="create-button__svg"
              >
                <path
                  d="M12 5v14M5 12h14"
                  stroke="white"
                  strokeWidth="2.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="create-button__label">
              Create User
            </span>
          </button>
        )}
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>
        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={fetchUsers}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredUsers.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      <div className="tt-table-container">
        <table className="tt-table">
          <thead>
            <tr>
              {headers.map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''
                        } ${sortConfig.direction === 'descending' && sortConfig.key === key ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
              {hasPrivilege(userData, 'USER_ROLE', 'USER_LIST_MODIFY') && (<th>Action</th>)}
            </tr>
          </thead>

          <tbody>
            {currentRows.map((user) => (
              <tr key={user.id || '-'}>
                <td>{user.name || '-'}</td>
                <td>{user.email || '-'}</td>
                <td>{user.user_type || '-'}</td>
                <td>{user.roles.join(', ') || '-'}</td>
                {hasPrivilege(userData, 'USER_ROLE', 'USER_LIST_MODIFY') && (
                  <td className="action-buttons">

                    <button className="edit-btn tooltip-btn" onClick={() => handleEditClick(user)}>  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.12 2.12 0 0 1 3.001 3.001L7.5 18.85l-4 1 1-4L16.862 3.487z" />
                    </svg>      <span className="tooltip-text">Edit</span></button>
                    <button className="delete-btn tooltip-btn" onClick={() => handleDeleteClick(user)}><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                    </svg>       <span className="tooltip-text">Delete</span></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      {showDeleteModal && (
        <DeleteUserModal
          userToDelete={userToDelete}
          handleConfirmDelete={handleConfirmDelete}
          closeDeleteModal={closeDeleteModal}
        />
      )}
    </div>
  );
};

export default UserList;